import { Head, Link, router, usePage } from '@inertiajs/react'
import { useEffect, useMemo, useState } from 'react'
import { CategoryType } from './types'
import {DollarSign, Plus,  Tag, TrendingUp, Users} from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import Form from './form'
import { Input } from '@/components/ui/input'

interface IndexProps {
  categories: CategoryType[]
  communities: {
    id: string
    name: string
  }[]
}

export default function Index({ categories, communities }: IndexProps) {
  const { flash } = usePage()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingCategory, setEditingCategory] =
    useState<CategoryType | null>(null)
  const [searchTerm, setSearchTerm] = useState(() => {
    if (typeof window === 'undefined') return ''
    return new URLSearchParams(window.location.search).get('q') ?? ''
  })

  const emptyCategory = useMemo(
    () => ({
      id: '',
      name: '',
      code: '',
      description: '',
      slug: '',
      status: 'active',
      community_id: '',
    }),
    []
  )

  const isEditing = Boolean(editingCategory)
  const formCategory = editingCategory ?? emptyCategory
  const formAction = isEditing
    ? `/system/categories/${formCategory.id}`
    : '/system/categories'
  const formMethod = isEditing ? 'patch' : 'post'
  const formTitle = isEditing ? 'Edit category' : 'New category'
  const formSubmitText = isEditing ? 'Update Category' : 'Create Category'

  const openCreateDialog = () => {
    setEditingCategory(null)
    setDialogOpen(true)
  }

  // const openEditDialog = (category: CategoryType) => {
  //   setEditingCategory(category)
  //   setDialogOpen(true)
  // }

  useEffect(() => {
    if (flash?.notice) {
      setDialogOpen(false)
      setEditingCategory(null)
    }
  }, [flash?.notice])

  useEffect(() => {
    const timeout = setTimeout(() => {
      router.get(
        '/system/categories',
        {
          q: searchTerm.trim() || undefined,
        },
        {
          preserveState: true,
          preserveScroll: true,
          replace: true,
          only: ['categories'],
        }
      )
    }, 300)

    return () => clearTimeout(timeout)
  }, [searchTerm])

  return (
    <>
      <Head title="Categories" />
      <div className="flex flex-col gap-6 p-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Category Management</h1>
            <p className="text-sm text-muted-foreground">Manage taxpayer categories and classifications</p>
          </div>
          <Button onClick={openCreateDialog}>
            <Plus className="mr-2 h-4 w-4" />
            Add Category
          </Button>
        </div>
        {/* Stats Overview */}
        <div className="grid gap-4 md:grid-cols-4">
          <div className="rounded-lg border border-border bg-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Categories</p>
                <p className="mt-2 text-3xl font-bold text-foreground">{categories.length}</p>
              </div>
              <Tag className="h-8 w-8 text-primary" />
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Taxpayers</p>
                <p className="mt-2 text-3xl font-bold text-foreground">0</p>
              </div>
              <Users className="h-8 w-8 text-info" />
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Collections</p>
                <p className="mt-2 text-3xl font-bold text-success">0 FCFA</p>
              </div>
              <DollarSign className="h-8 w-8 text-success" />
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Avg per Taxpayer</p>
                <p className="mt-2 text-3xl font-bold text-warning">0 FCFA</p>
              </div>
              <TrendingUp className="h-8 w-8 text-warning" />
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Input
                  placeholder="Search by name or code"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
              />
            </div>
            <button className="rounded-lg border border-border px-3 py-2 text-sm font-medium text-foreground hover:bg-secondary transition-colors">Filter</button>
          </div>
        </div>

        {/* Categories Table */}
        <div className="rounded-lg border border-border bg-card overflow-hidden">
          <table className="w-full">
            <thead className="border-b border-border bg-secondary">
            <tr>
              <th className="px-6 py-3 text-left text-sm font-medium text-muted-foreground">Category</th>
              <th className="px-6 py-3 text-left text-sm font-medium text-muted-foreground">Description</th>
              <th className="px-6 py-3 text-left text-sm font-medium text-muted-foreground">Taxpayers</th>
              <th className="px-6 py-3 text-left text-sm font-medium text-muted-foreground">Collections</th>
              <th className="px-6 py-3 text-left text-sm font-medium text-muted-foreground">Status</th>
              <th className="px-6 py-3 text-left text-sm font-medium text-muted-foreground">Actions</th>
            </tr>
            </thead>
            <tbody className="divide-y divide-border">
            {categories.map((category) => (
                <tr key={category.id} className="transition-colors hover:bg-secondary/50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="rounded-lg bg-primary/10 p-2">
                        <Tag className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-foreground">{category.name}</p>
                        <p className="text-xs text-muted-foreground font-mono">{category.code}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-sm text-muted-foreground">{category.description}</p>
                  </td>
                  <td className="px-6 py-4">
                    <p className="font-medium text-foreground">0</p>
                  </td>
                  <td className="px-6 py-4">
                    <p className="font-medium text-success">0 FCFA</p>
                  </td>
                  <td className="px-6 py-4">
                    <span className="inline-flex rounded-full bg-success/10 px-3 py-1 text-xs font-medium text-success">{category.status}</span>
                  </td>
                  <td className="px-6 py-4">
                    <Link href={`/system/categories/${category.id}`}>
                      <button className="text-sm font-medium text-primary hover:text-primary/80 transition-colors">View Details</button>
                    </Link>
                  </td>
                </tr>
            ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{formTitle}</DialogTitle>
          </DialogHeader>
          <Form
            key={`${isEditing ? formCategory.id : 'new'}`}
            category={formCategory}
            communities={communities}
            action={formAction}
            method={formMethod}
            submitText={formSubmitText}
            onSuccess={() => {
              setDialogOpen(false)
              setEditingCategory(null)
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  )
}
